// Purpose: circular arc gauge. Admin dashboard uses it for "DLQ /
// dangerous threshold" or "cert days remaining" style tiles. ~50 lines,
// no chart lib (§14 Q10).

export interface GaugeProps {
  value: number;
  max: number;
  label?: string;
  size?: number;
  className?: string;
  // Override the auto color. Default: >=80% danger, >=50% warning, else success.
  // Hex values chosen to read well in both light and dark themes; SVG
  // `stroke`/`fill` cannot consume CSS vars without inline style(), and
  // these mid-tones render cleanly on either bg.
  color?: string;
}

function defaultColor(pct: number): string {
  if (pct >= 0.8) return '#b91c1c'; // status-danger equivalent
  if (pct >= 0.5) return '#b45309'; // status-warning equivalent
  return '#047857'; // status-success equivalent
}

export function Gauge(props: GaugeProps) {
  const { value, max, label, size = 120, className } = props;
  const safe = Math.max(0, Math.min(1, max > 0 ? value / max : 0));
  const color = props.color ?? defaultColor(safe);
  const r = size / 2 - 8;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  const arc = circ * 0.75; // 3/4 arc
  const offset = arc * (1 - safe);

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label={`gauge ${value} of ${max}`}
    >
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="#e2e8f0"
        strokeWidth={10}
        strokeDasharray={`${arc} ${circ}`}
        transform={`rotate(135 ${cx} ${cy})`}
      />
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={10}
        strokeLinecap="round"
        strokeDasharray={`${arc} ${circ}`}
        strokeDashoffset={offset}
        transform={`rotate(135 ${cx} ${cy})`}
      />
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={size * 0.2}
        fill="#0f172a"
        fontWeight={600}
      >
        {value}
      </text>
      {label ? (
        <text x={cx} y={cy + size * 0.22} textAnchor="middle" fontSize={10} fill="#64748b">
          {label}
        </text>
      ) : null}
    </svg>
  );
}
